import { useState } from 'react';
import {
  User,
  Plane,
  Terminal,
  Loader2,
  ArrowLeft,
  RefreshCw,
  Copy,
  Check,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import type { FlightSegment, Passenger, Fare, FQPResponse, FQQResponse } from '@/types/pricing';
import { generateFQPString } from '@/utils/fqpGenerator';
import { formatAmadeusResponse, formatPrice } from '@/utils/amadeusParser';

interface ResultsStepProps {
  passenger: Passenger;
  ticketingDate: string;
  segments: FlightSegment[];
  onBack: () => void;
  onReset: () => void;
  onExecuteFQP: (command: string) => Promise<FQPResponse>;
  onExecuteFQQ: (fareNumber: string) => Promise<FQQResponse>;
}

export function ResultsStep({
  passenger,
  ticketingDate,
  segments,
  onBack,
  onReset,
  onExecuteFQP,
  onExecuteFQQ,
}: ResultsStepProps) {
  const [fqpLoading, setFqpLoading] = useState(false);
  const [fqpResponse, setFqpResponse] = useState<FQPResponse | null>(null);
  const [fqpError, setFqpError] = useState<string | null>(null);

  const [selectedFare, setSelectedFare] = useState<string | null>(null);

  const [fqqLoading, setFqqLoading] = useState(false);
  const [fqqResponse, setFqqResponse] = useState<FQQResponse | null>(null);
  const [fqqError, setFqqError] = useState<string | null>(null);

  const [copied, setCopied] = useState(false);

  const fqpCommand = generateFQPString(segments, ticketingDate);

  const handleCopyCommand = async () => {
    await navigator.clipboard.writeText(fqpCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExecuteFQP = async () => {
    setFqpLoading(true);
    setFqpError(null);
    setFqpResponse(null);
    setSelectedFare(null);
    setFqqResponse(null);

    try {
      // Uses REST API with warm Amadeus session - should be fast
      const result = await onExecuteFQP(fqpCommand);
      setFqpResponse(result);

      // Auto-select first fare if available
      if (result.fares && result.fares.length > 0) {
        setSelectedFare(result.fares[0].id);
      }
    } catch (err) {
      setFqpError(err instanceof Error ? err.message : 'Failed to execute FQP command');
    } finally {
      setFqpLoading(false);
    }
  };

  const handleGetFareDetails = async () => {
    if (!selectedFare) return;

    setFqqLoading(true);
    setFqqError(null);
    setFqqResponse(null);

    try {
      const result = await onExecuteFQQ(selectedFare);
      setFqqResponse(result);
    } catch (err) {
      setFqqError(err instanceof Error ? err.message : 'Failed to get fare details');
    } finally {
      setFqqLoading(false);
    }
  };

  const fares = fqpResponse?.fares || [];
  const hasNoFares = fqpResponse && fares.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Fare Quote Results</h2>
        <p className="mt-1 text-sm text-slate-600">
          Review your data and execute the FQP command to get available fares.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Summary */}
        <div className="space-y-4">
          {/* Passenger Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4 text-blue-600" />
                Passenger
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Name</span>
                <span className="font-medium">{passenger.firstName} {passenger.lastName}</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-slate-500">Ticketing Date</span>
                <span className="font-medium">{ticketingDate}</span>
              </div>
            </CardContent>
          </Card>

          {/* Flight Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Plane className="h-4 w-4 text-blue-600" />
                Itinerary ({segments.length} segment{segments.length > 1 ? 's' : ''})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {segments.map((segment, index) => (
                <div key={segment.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{segment.origin}</span>
                    <span className="text-slate-400">→</span>
                    <span className="font-bold">{segment.destination}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {segment.airline} {segment.bookingClass}
                    </Badge>
                    <span className="text-slate-500">{segment.flightDate}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* FQP Command */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Terminal className="h-4 w-4 text-blue-600" />
                FQP Command
              </CardTitle>
              <CardDescription>Amadeus fare quote pricing command</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-slate-100 p-3 rounded-lg text-xs font-mono text-slate-700 overflow-x-auto">
                  {fqpCommand}
                </pre>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6"
                  onClick={handleCopyCommand}
                >
                  {copied ? (
                    <Check className="h-3 w-3 text-green-600" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Edit Data
            </Button>
            <Button variant="outline" onClick={onReset}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Start Over
            </Button>
          </div>
        </div>

        {/* Right Column - Results */}
        <div className="space-y-4">
          {/* Execute Button / Loading */}
          {!fqpResponse && !fqpLoading && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <p className="text-sm text-slate-600">
                    Ready to fetch live fare quotes from Amadeus
                  </p>
                  <Button
                    size="lg"
                    onClick={handleExecuteFQP}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Get Fare Quotes
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {fqpLoading && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-center gap-3 py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="text-sm font-medium text-slate-800">
                    Fetching fare quotes...
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error State */}
          {fqpError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{fqpError}</AlertDescription>
            </Alert>
          )}

          {/* Results */}
          {fqpResponse && (
            <>
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                {hasNoFares ? (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                    No fares found
                  </Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    {fares.length} fare{fares.length !== 1 ? 's' : ''} found
                  </Badge>
                )}
                <span className="text-xs text-slate-500">{fqpResponse.meta.message}</span>
              </div>

              {/* Fares Table */}
              {fares.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Available Fares</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RadioGroup
                      value={selectedFare || ''}
                      onValueChange={setSelectedFare}
                    >
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12"></TableHead>
                            <TableHead>Fare #</TableHead>
                            <TableHead>Fare Basis</TableHead>
                            <TableHead className="text-right">Price</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {fares.map((fare) => (
                            <TableRow
                              key={fare.id}
                              className="cursor-pointer"
                              onClick={() => setSelectedFare(fare.id)}
                            >
                              <TableCell>
                                <RadioGroupItem value={fare.id} id={`fare-${fare.id}`} />
                              </TableCell>
                              <TableCell className="font-mono">{fare.id}</TableCell>
                              <TableCell className="font-mono">{fare.farebase}</TableCell>
                              <TableCell className="text-right font-semibold">
                                {formatPrice(fare.price)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </RadioGroup>

                    {/* Get Details Button */}
                    <div className="mt-4 flex justify-end">
                      <Button
                        onClick={handleGetFareDetails}
                        disabled={!selectedFare || fqqLoading}
                      >
                        {fqqLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          'Get Fare Details'
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* FQQ Response */}
              {fqqError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{fqqError}</AlertDescription>
                </Alert>
              )}

              {fqqResponse && (
                <Card className="border-green-200 bg-green-50/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">
                      Fare Details (FQQ{selectedFare})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600">{fqqResponse.meta.message}</p>

                      {fqqResponse.fares && fqqResponse.fares.length > 0 && (
                        <div className="space-y-2">
                          {fqqResponse.fares.map((fare, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>Fare {fare.id}: {fare.farebase}</span>
                              <span className="font-semibold">{formatPrice(fare.price)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Raw Response */}
                      <div className="mt-4">
                        <p className="text-xs text-slate-500 mb-2">Raw Amadeus Response:</p>
                        <pre className="bg-white p-3 rounded border text-xs font-mono text-slate-600 overflow-x-auto max-h-48 overflow-y-auto">
                          {formatAmadeusResponse(fqqResponse.raw_response || 'No response')}
                        </pre>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Raw FQP Response (collapsed by default) */}
              {fqpResponse.raw_response && (
                <details className="mt-4">
                  <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                    Show raw FQP response
                  </summary>
                  <pre className="mt-2 bg-slate-100 p-3 rounded text-xs font-mono text-slate-600 overflow-x-auto max-h-48 overflow-y-auto">
                    {formatAmadeusResponse(fqpResponse.raw_response)}
                  </pre>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
